import React, { useEffect, useState } from "react";
import { X, Image as ImageIcon, Tag, Eye, EyeOff, ChefHat } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";

/**
 * props:
 * - isOpen: boolean
 * - onClose: () => void
 * - product: Product | null
 */
export default function ProductPreviewModal({ isOpen, onClose, product }) {
  const { t, dir } = useI18n();
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  const [activeImage, setActiveImage] = useState(0);
  useEffect(() => { setActiveImage(0); }, [product?.id]);

  const images = Array.isArray(product?.images) ? product.images : [];

  return (
    <AnimatePresence>
      {isOpen && product && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" dir={dir}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white w-full max-w-md max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Hero Image */}
            <div className="relative h-56 sm:h-64 bg-gray-100 shrink-0 overflow-hidden">
              {images[activeImage] ? (
                <motion.img
                  key={activeImage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  src={images[activeImage].url}
                  className="w-full h-full object-cover"
                  alt={product.name}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                  <ImageIcon size={48} className="text-gray-200" />
                </div>
              )}

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-gray-500 hover:text-[#c32228] transition-all shadow-md"
              >
                <X size={18} />
              </button>

              {/* Category badge */}
              <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm flex items-center gap-1.5 shadow-sm">
                <Tag size={12} className="text-[#d6ad60]" />
                <span className="text-[11px] font-extrabold text-gray-700">
                  {product.categoryName || t("modal.noCategory")}
                </span>
              </div>

              {/* Price badge on image */}
              <div className="absolute bottom-3 left-3 px-4 py-2 rounded-2xl bg-[#c32228] shadow-lg">
                <div className="flex items-baseline gap-1 text-white">
                  <span className="text-xl font-black font-serif">
                    {Number(product.price || 0).toLocaleString()}
                  </span>
                  <span className="text-[10px] font-bold opacity-80">
                    {product.currency || "₺"}
                  </span>
                </div>
              </div>

              {/* Status badge */}
              <div className={`absolute bottom-3 right-3 px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm backdrop-blur-sm ${product.isActive
                  ? "bg-emerald-500/90 text-white"
                  : "bg-gray-500/90 text-white"
                }`}>
                {product.isActive ? <Eye size={13} /> : <EyeOff size={13} />}
                <span className="text-[11px] font-extrabold">
                  {product.isActive ? t("modal.available") : t("modal.hidden")}
                </span>
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 px-5 py-3 border-b border-gray-100 overflow-x-auto scrollbar-hide">
                {images.map((img, index) => (
                  <button
                    key={img.id ?? index}
                    onClick={() => setActiveImage(index)}
                    type="button"
                    className={`relative w-12 h-12 rounded-xl overflow-hidden border-2 transition-all shrink-0 ${activeImage === index
                        ? "border-[#c32228] shadow-md scale-105"
                        : "border-gray-200 opacity-50 hover:opacity-100"
                      }`}
                  >
                    <img src={img.url} className="w-full h-full object-cover" alt="" />
                  </button>
                ))}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {/* Name */}
              <h2 className="text-xl font-black text-gray-900 leading-snug">
                {product.name || t("modal.noName")}
              </h2>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[#c32228]">
                  <ChefHat size={16} />
                  <span className="text-xs font-extrabold tracking-wide">{t("modal.descriptionLabel")}</span>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed pr-1">
                  {product.description ||
                    t("modal.defaultDescription")}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl font-extrabold text-white text-sm transition-all hover:shadow-lg active:scale-[0.98]"
                style={{ background: "linear-gradient(135deg, #c32228 0%, #8a1418 100%)" }}
              >
                {t("modal.close")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
